import React, { useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import {
  Container,
  Box,
  Typography,
  Chip,
  Modal,
  IconButton,
  CircularProgress,
  Pagination,
  Button,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import GroupIcon from "@mui/icons-material/Group";
import CampaignIcon from "@mui/icons-material/Campaign";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReplayIcon from "@mui/icons-material/Replay";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import StatusPill from "./StatusPill";
import CampaignRow from "./CampaignRow";

// ── helpers ──────────────────────────────────────────────────────────────────
export const cTimeFormat = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const initials = (name = "") =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const avatarColor = (str = "") => {
  const palette = [
    "#E07B39",
    "#4FA8D5",
    "#7DC17A",
    "#C47FD4",
    "#D4A84B",
    "#5BB8A0",
    "#D45F5F",
    "#6E83D4",
  ];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

// ── sub-components ───────────────────────────────────────────────────────────
export const Avatar = ({ name, size = 36 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: avatarColor(name),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: size * 0.35,
      fontWeight: 700,
      color: "#fff",
      flexShrink: 0,
    }}
  >
    {initials(name)}
  </Box>
);

const MetaChip = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
      {React.cloneElement(icon, { sx: { fontSize: 13, color: "#888" } })}
      <Typography
        sx={{
          fontSize: 10,
          color: "#888",
          textTransform: "uppercase",
          letterSpacing: 1,
          fontFamily: "'IBM Plex Mono',monospace",
        }}
      >
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontSize: 13,
        color: "#222",
        fontFamily: "'IBM Plex Mono',monospace",
        fontWeight: 500,
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ── Extraction Status Badge ───────────────────────────────────────────────────
const extractionStatusMap = {
  in_progress: {
    label: "Extracting",
    color: "#4FA8D5",
    bg: "rgba(79,168,213,.12)",
    Icon: HourglassEmptyIcon,
  },
  completed: {
    label: "Extracted",
    color: "#2ECC71",
    bg: "rgba(46,204,113,.12)",
    Icon: CheckCircleOutlineIcon,
  },
  failed_generation: {
    label: "Gen Failed",
    color: "#E74C3C",
    bg: "rgba(231,76,60,.12)",
    Icon: ErrorOutlineIcon,
  },
  failed_extraction: {
    label: "Zip Failed",
    color: "#E74C3C",
    bg: "rgba(231,76,60,.12)",
    Icon: ErrorOutlineIcon,
  },
};

const ExtractionStatusBadge = ({ status }) => {
  const s = extractionStatusMap[status];
  if (!s) return null;
  const { label, color, bg, Icon } = s;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.1,
        py: 0.3,
        borderRadius: "20px",
        background: bg,
        border: `1px solid ${color}44`,
      }}
    >
      <Icon sx={{ fontSize: 11, color }} />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color,
          fontFamily: "'IBM Plex Mono',monospace",
          letterSpacing: 0.5,
        }}
      >
        {label.toUpperCase()}
      </Typography>
    </Box>
  );
};

// ── Retry Timeline ────────────────────────────────────────────────────────────
const RetryTimeline = ({ generateRetries = [], extractRetries = [] }) => {
  const hasAny = generateRetries.length > 0 || extractRetries.length > 0;
  if (!hasAny) return null;

  const Section = ({ label, retries }) => {
    if (!retries?.length) return null;
    return (
      <Box>
        <Typography
          sx={{
            fontSize: 9.5,
            color: "#aaa",
            letterSpacing: 1,
            textTransform: "uppercase",
            fontFamily: "'IBM Plex Mono',monospace",
            mb: 0.75,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {retries.map((r, i) => {
            const isSuccess = r.status === "success";
            const color = isSuccess ? "#2ECC71" : "#E74C3C";
            return (
              <Box
                key={i}
                sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: color,
                      boxShadow: `0 0 4px ${color}88`,
                    }}
                  />
                  {i < retries.length - 1 && (
                    <Box
                      sx={{
                        width: 1,
                        flex: 1,
                        minHeight: 10,
                        background: "rgba(0,0,0,.1)",
                        mt: 0.25,
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: "#444",
                      fontFamily: "'IBM Plex Mono',monospace",
                    }}
                  >
                    {r.attempt === 0 ? "Initial" : `Retry ${r.attempt}`}
                    <Box
                      component="span"
                      sx={{ color, fontWeight: 700, ml: 0.75 }}
                    >
                      · {isSuccess ? "✓ ok" : "✗ fail"}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 9.5,
                      color: "#aaa",
                      fontFamily: "'IBM Plex Mono',monospace",
                    }}
                  >
                    {cTimeFormat(r.ts)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        px: 1.75,
        py: 1.5,
        background: "rgba(0,0,0,.025)",
        border: "1px solid rgba(0,0,0,.06)",
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Typography
        sx={{
          fontSize: 9.5,
          color: "#bbb",
          letterSpacing: 1.5,
          textTransform: "uppercase",
          fontFamily: "'IBM Plex Mono',monospace",
        }}
      >
        Retry Log
      </Typography>
      <Section label="Report Generation" retries={generateRetries} />
      <Section label="ZIP Extraction" retries={extractRetries} />
    </Box>
  );
};

// ── Detail Modal ──────────────────────────────────────────────────────────────
const DetailModal = ({ item, onClose, onExtracted }) => {
  const [extracting, setExtracting] = useState(false);
  const [extractMsg, setExtractMsg] = useState(null);

  if (!item) return null;
  const agents = item.agentRows?.patchList ?? [];
  const pl = item.launchedPayload ?? {};
  const ts = item.createdAt ?? item.launchedAt;
  const dndList = item.dndList ?? [];
  const exStatus = item.extractionStatus;
  const isInProgress = exStatus === "in_progress";

  const handleExtractDnd = async () => {
    setExtracting(true);
    setExtractMsg(null);
    try {
      const res = await fetch(
        `${API_ENDPOINT}/voice/dnd-list-extract-request/${item.campaignId}`,
        { method: "GET", ...getHeader() }
      );
      const data = await res.json();

      if (res.ok) {
        setExtractMsg({
          type: "success",
          text: data.message || "Extraction started!",
        });
        onExtracted?.();
      } else {
        setExtractMsg({
          type: "error",
          text: data.message || "Failed to start extraction.",
        });
      }
    } catch {
      setExtractMsg({ type: "error", text: "Network error occurred." });
    } finally {
      setExtracting(false);
    }
  };

  return (
    <Modal open onClose={onClose} closeAfterTransition>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: { xs: "95vw", sm: 520 },
          maxHeight: "88vh",
          overflowY: "auto",
          background: "#ffffff",
          border: "1px solid rgba(0,0,0,.1)",
          borderRadius: 2,
          outline: "none",
          fontFamily: "'IBM Plex Mono',monospace",
          boxShadow: "0 32px 80px rgba(0,0,0,.15)",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { background: "#ccc", borderRadius: 4 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2.5,
            borderBottom: "1px solid rgba(0,0,0,.08)",
            position: "sticky",
            top: 0,
            background: "#ffffff",
            zIndex: 1,
          }}
        >
          <Avatar name={item.customerId} size={44} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: "#111",
                fontFamily: "'IBM Plex Mono',monospace",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.campaignName}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: "#666",
                fontFamily: "'IBM Plex Mono',monospace",
              }}
            >
              {item.customerId}
            </Typography>
          </Box>
          <StatusPill status={item.status ?? "launched"} />
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#777",
              "&:hover": { color: "#333", background: "rgba(0,0,0,.05)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {/* Core meta grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              background: "rgba(0,0,0,.02)",
              border: "1px solid rgba(0,0,0,.05)",
              borderRadius: 1.5,
              px: 2.5,
              py: 2,
            }}
          >
            <MetaChip
              icon={<CampaignIcon />}
              label="Campaign ID"
              value={`#${item.campaignId}`}
            />
            <MetaChip
              icon={<LaunchIcon />}
              label="Base File"
              value={`#${item.baseFileId}`}
            />
            <MetaChip
              icon={<ScheduleIcon />}
              label={item.createdAt ? "Created" : "Launched"}
              value={cTimeFormat(ts)}
            />
            {pl.scheduleTime && (
              <MetaChip
                icon={<ScheduleIcon />}
                label="Scheduled"
                value={pl.scheduleTime}
              />
            )}
          </Box>

          {/* DND Section */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BlockIcon sx={{ fontSize: 14, color: "#e74c3c" }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontFamily: "'IBM Plex Mono',monospace",
                  }}
                >
                  DND List {dndList.length > 0 && `(${dndList.length})`}
                </Typography>
              </Box>
              {exStatus && <ExtractionStatusBadge status={exStatus} />}
            </Box>

            {dndList.length > 0 ? (
              <Box>
                <Box
                  sx={{
                    background: "rgba(0,0,0,.02)",
                    border: "1px solid rgba(0,0,0,.05)",
                    borderRadius: 1.5,
                    px: 2,
                    py: 2,
                    maxHeight: 120,
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {dndList.map((num, idx) => (
                    <Chip
                      key={idx}
                      label={num}
                      size="small"
                      sx={{
                        fontFamily: "'IBM Plex Mono',monospace",
                        fontSize: 11,
                        background: "rgba(231,76,60,.1)",
                        color: "#c0392b",
                        border: "1px solid rgba(231,76,60,.2)",
                      }}
                    />
                  ))}
                </Box>
                {item.extractionStartedAt && (
                  <Box sx={{ display: "flex", gap: 2, mt: 1.25 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#bbb",
                        fontFamily: "'IBM Plex Mono',monospace",
                      }}
                    >
                      Started: {cTimeFormat(item.extractionStartedAt)}
                    </Typography>
                    {item.extractionEndedAt && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "#bbb",
                          fontFamily: "'IBM Plex Mono',monospace",
                        }}
                      >
                        · Ended: {cTimeFormat(item.extractionEndedAt)}
                      </Typography>
                    )}
                  </Box>
                )}
                <RetryTimeline
                  generateRetries={item.generateRetries}
                  extractRetries={item.extractRetries}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  background: "rgba(0,0,0,.02)",
                  border: "1px solid rgba(0,0,0,.05)",
                  borderRadius: 1.5,
                  px: 2,
                  py: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#666",
                    fontFamily: "'IBM Plex Mono',monospace",
                  }}
                >
                  {isInProgress
                    ? "Extraction is running in the background…"
                    : exStatus?.startsWith("failed")
                    ? "Extraction failed after all retries."
                    : "No DND records found for this campaign."}
                </Typography>
                {item.extractionStartedAt && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#bbb",
                        fontFamily: "'IBM Plex Mono',monospace",
                      }}
                    >
                      Started: {cTimeFormat(item.extractionStartedAt)}
                    </Typography>
                    {item.extractionEndedAt && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "#bbb",
                          fontFamily: "'IBM Plex Mono',monospace",
                        }}
                      >
                        Ended: {cTimeFormat(item.extractionEndedAt)}
                      </Typography>
                    )}
                  </Box>
                )}
                <RetryTimeline
                  generateRetries={item.generateRetries}
                  extractRetries={item.extractRetries}
                />
                {exStatus !== "completed" && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExtractDnd}
                    disabled={extracting || isInProgress}
                    startIcon={
                      extracting || isInProgress ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : null
                    }
                    sx={{
                      fontFamily: "'IBM Plex Mono',monospace",
                      fontSize: 11,
                      textTransform: "none",
                      color: "#E07B39",
                      borderColor: "rgba(224,123,57,.5)",
                      "&:hover": {
                        borderColor: "#E07B39",
                        background: "rgba(224,123,57,.05)",
                      },
                      "&.Mui-disabled": {
                        color: "rgba(224,123,57,.4)",
                        borderColor: "rgba(224,123,57,.2)",
                      },
                    }}
                  >
                    {isInProgress
                      ? "Extraction in progress…"
                      : extracting
                      ? "Requesting…"
                      : exStatus?.startsWith("failed")
                      ? "Retry DND Extraction"
                      : "Request DND Extraction"}
                  </Button>
                )}
                {extractMsg && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontFamily: "'IBM Plex Mono',monospace",
                      color:
                        extractMsg.type === "success" ? "#2ECC71" : "#E74C3C",
                    }}
                  >
                    {extractMsg.text}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Agents */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}
            >
              <GroupIcon sx={{ fontSize: 14, color: "#888" }} />
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#888",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontFamily: "'IBM Plex Mono',monospace",
                }}
              >
                Agent Groups
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {agents.map((a, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "rgba(0,0,0,.02)",
                    border: "1px solid rgba(0,0,0,.05)",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: "#222",
                        fontFamily: "'IBM Plex Mono',monospace",
                        fontWeight: 600,
                      }}
                    >
                      {a.groupName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#666",
                        fontFamily: "'IBM Plex Mono',monospace",
                      }}
                    >
                      Group ID: {a.groupId}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: "rgba(224,123,57,.15)",
                      border: "1px solid rgba(224,123,57,.3)",
                      borderRadius: 1,
                      px: 1,
                      py: 0.3,
                      fontSize: 11,
                      color: "#c66222",
                      fontFamily: "'IBM Plex Mono',monospace",
                      fontWeight: 700,
                    }}
                  >
                    DTMF {a.agentDtmf}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Settings row */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {item.executiveSettings !== undefined && (
              <Chip
                label={`Executive: ${item.executiveSettings ? "ON" : "OFF"}`}
                size="small"
                sx={{
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 11,
                  background: item.executiveSettings
                    ? "rgba(94,170,100,.15)"
                    : "rgba(0,0,0,.05)",
                  color: item.executiveSettings ? "#4b8c49" : "#666",
                  border: `1px solid ${
                    item.executiveSettings
                      ? "rgba(125,193,122,.4)"
                      : "rgba(0,0,0,.1)"
                  }`,
                }}
              />
            )}
            {pl.retries && (
              <Chip
                label={`Retries: ${pl.retries}`}
                size="small"
                sx={{
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 11,
                  background: "rgba(0,0,0,.05)",
                  color: "#666",
                  border: "1px solid rgba(0,0,0,.1)",
                }}
              />
            )}
            {pl.retryInterval && (
              <Chip
                label={`Retry interval: ${pl.retryInterval}s`}
                size="small"
                sx={{
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 11,
                  background: "rgba(0,0,0,.05)",
                  color: "#666",
                  border: "1px solid rgba(0,0,0,.1)",
                }}
              />
            )}
            {pl.menuWaitTime && (
              <Chip
                label={`Menu wait: ${pl.menuWaitTime}s`}
                size="small"
                sx={{
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 11,
                  background: "rgba(0,0,0,.05)",
                  color: "#666",
                  border: "1px solid rgba(0,0,0,.1)",
                }}
              />
            )}
            {pl.webhook && (
              <Chip
                label={`Webhook: ${pl.webhookId ?? "ON"}`}
                size="small"
                sx={{
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 11,
                  background: "rgba(79,168,213,.12)",
                  color: "#2e7b9e",
                  border: "1px solid rgba(79,168,213,.3)",
                }}
              />
            )}
          </Box>

          {/* Launch response */}
          {item.launchedResponse && (
            <Box
              sx={{
                background: "rgba(46,204,113,.1)",
                border: "1px solid rgba(46,204,113,.25)",
                borderRadius: 1.5,
                px: 2,
                py: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#1e9952",
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                ✓ LAUNCH RESPONSE
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#444",
                  fontFamily: "'IBM Plex Mono',monospace",
                }}
              >
                {item.launchedResponse.message}
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: 10,
              color: "#bbb",
              fontFamily: "'IBM Plex Mono',monospace",
              textAlign: "right",
            }}
          >
            _id: {item._id}
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};

// ── Repush Confirmation Modal ─────────────────────────────────────────────────
const RepushConfirmModal = ({
  item,
  onClose,
  onConfirm,
  submitting,
  result,
}) => {
  if (!item) return null;

  return (
    <Modal open onClose={submitting ? undefined : onClose} closeAfterTransition>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: { xs: "92vw", sm: 440 },
          background: "#ffffff",
          border: "1px solid rgba(0,0,0,.1)",
          borderRadius: 2,
          outline: "none",
          fontFamily: "'IBM Plex Mono',monospace",
          boxShadow: "0 32px 80px rgba(0,0,0,.15)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 2.25,
            borderBottom: "1px solid rgba(0,0,0,.08)",
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "rgba(224,123,57,.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ReplayIcon sx={{ fontSize: 18, color: "#E07B39" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 14.5,
                fontWeight: 700,
                color: "#111",
                fontFamily: "'IBM Plex Mono',monospace",
              }}
            >
              Repush Campaign?
            </Typography>
            <Typography
              sx={{
                fontSize: 11.5,
                color: "#666",
                fontFamily: "'IBM Plex Mono',monospace",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.campaignName}
            </Typography>
          </Box>
          {!submitting && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "#777",
                "&:hover": { color: "#333", background: "rgba(0,0,0,.05)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Body */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              background: "rgba(231,76,60,.08)",
              border: "1px solid rgba(231,76,60,.25)",
              borderRadius: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <WarningAmberIcon
              sx={{ fontSize: 18, color: "#E74C3C", flexShrink: 0, mt: 0.1 }}
            />
            <Typography
              sx={{
                fontSize: 12,
                color: "#5c1f1f",
                fontFamily: "'IBM Plex Mono',monospace",
                lineHeight: 1.6,
              }}
            >
              This will re-initiate calls for this campaign. Please make sure
              you really intend to do this — repushing can disturb users who may
              have already been contacted, and repeated calls to the same
              numbers may cause complaints.
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 12,
              color: "#444",
              fontFamily: "'IBM Plex Mono',monospace",
              lineHeight: 1.6,
            }}
          >
            The repush will only target numbers that have not yet been answered
            — i.e. numbers whose current status is one of:
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {["Initiated", "Ringing", "Not Initiated"].map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                sx={{
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 11,
                  background: "rgba(79,168,213,.12)",
                  color: "#2e7b9e",
                  border: "1px solid rgba(79,168,213,.3)",
                }}
              />
            ))}
          </Box>

          <Typography
            sx={{
              fontSize: 11,
              color: "#888",
              fontFamily: "'IBM Plex Mono',monospace",
            }}
          >
            Campaign #{item.campaignId}
          </Typography>

          {result && (
            <Typography
              sx={{
                fontSize: 11.5,
                fontFamily: "'IBM Plex Mono',monospace",
                color: result.type === "success" ? "#2ECC71" : "#E74C3C",
              }}
            >
              {result.text}
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.25,
            px: 3,
            py: 2,
            borderTop: "1px solid rgba(0,0,0,.08)",
          }}
        >
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              fontFamily: "'IBM Plex Mono',monospace",
              fontSize: 12,
              textTransform: "none",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <ReplayIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              fontFamily: "'IBM Plex Mono',monospace",
              fontSize: 12,
              textTransform: "none",
              background: "#E07B39",
              boxShadow: "none",
              "&:hover": { background: "#c9692e", boxShadow: "none" },
              "&.Mui-disabled": {
                background: "rgba(224,123,57,.4)",
                color: "#fff",
              },
            }}
          >
            {submitting ? "Repushing…" : "Yes, Repush"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const VoiceCampaignsList = () => {
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);

  // Repush confirmation flow state
  const [repushTarget, setRepushTarget] = useState(null); // item pending confirmation
  const [repushSubmitting, setRepushSubmitting] = useState(false);
  const [repushResult, setRepushResult] = useState(null);

  const { data, isLoading, isError, isFetching, error, refetch } = useApiEp(
    `/voice/get-campaigns-list?page=${page}`,
    true,
    1,
    (latestData) =>
      latestData?.data?.some((c) => c.extractionStatus === "in_progress")
        ? 10_000
        : false
  );

  // Opens the confirmation modal instead of calling the API directly
  const handleRepushAction = (item) => {
    setRepushResult(null);
    setRepushTarget(item);
  };

  const closeRepushModal = () => {
    if (repushSubmitting) return;
    setRepushTarget(null);
    setRepushResult(null);
  };

  const confirmRepush = async () => {
    if (!repushTarget) return;
    setRepushSubmitting(true);
    setRepushResult(null);
    try {
      const res = await fetch(
        `${API_ENDPOINT}/voice/repush-campaign/${repushTarget._id}`,
        { method: "GET", ...getHeader() }
      );
      const data = await res.json();

      if (res.ok) {
        setRepushResult({
          type: "success",
          text: data.message || "Campaign repush initiated successfully!",
        });
        refetch();
        // Auto-close shortly after a successful repush
        setTimeout(() => {
          setRepushTarget(null);
          setRepushResult(null);
        }, 1200);
      } else {
        setRepushResult({
          type: "error",
          text: data.message || "Failed to repush campaign.",
        });
      }
    } catch {
      setRepushResult({ type: "error", text: "Network error occurred." });
    } finally {
      setRepushSubmitting(false);
    }
  };

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');`}</style>

      <Container
        maxWidth="md"
        sx={{ py: 4, fontFamily: "'IBM Plex Mono',monospace" }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111",
                fontFamily: "'IBM Plex Mono',monospace",
                letterSpacing: -0.5,
              }}
            >
              Voice Campaigns
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {data?.totalItems !== undefined && (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#666",
                    fontFamily: "'IBM Plex Mono',monospace",
                  }}
                >
                  {data.totalItems.toLocaleString()} total · page {page}/
                  {data.totalPages || 1}
                </Typography>
              )}
              <Tooltip title="Refresh" placement="left">
                <span>
                  <IconButton
                    onClick={() => refetch()}
                    disabled={isFetching}
                    size="small"
                    sx={{
                      color: "#aaa",
                      transition: "color .2s",
                      "&:hover": {
                        color: "#E07B39",
                        background: "rgba(224,123,57,.06)",
                      },
                      "& svg": {
                        animation: isFetching
                          ? "spin .7s linear infinite"
                          : "none",
                        "@keyframes spin": {
                          "100%": { transform: "rotate(360deg)" },
                        },
                      },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Box
            sx={{
              mt: 1,
              height: 2,
              width: 40,
              background: "#E07B39",
              borderRadius: 1,
            }}
          />
        </Box>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: "#E07B39" }} />
          </Box>
        )}
        {isError && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              color: "#E74C3C",
              fontFamily: "'IBM Plex Mono',monospace",
              fontSize: 13,
            }}
          >
            {error?.message ?? "Failed to load campaigns"}
          </Box>
        )}

        {!isLoading && !isError && data?.data && (
          <>
            {isFetching && (
              <Box
                sx={{
                  height: 2,
                  mb: 1.5,
                  borderRadius: 1,
                  overflow: "hidden",
                  background: "rgba(224,123,57,.1)",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: "35%",
                    background: "#E07B39",
                    borderRadius: 1,
                    animation: "sweep 1.1s ease-in-out infinite",
                    "@keyframes sweep": {
                      "0%": { marginLeft: "-35%" },
                      "100%": { marginLeft: "100%" },
                    },
                  }}
                />
              </Box>
            )}

            <Box
              sx={{
                background: "#ffffff",
                border: "1px solid rgba(0,0,0,.1)",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,.04)",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr auto auto",
                  gap: 2,
                  px: 2.5,
                  py: 1.25,
                  borderBottom: "1px solid rgba(0,0,0,.08)",
                  background: "rgba(0,0,0,.02)",
                }}
              >
                {["", "Campaign", "Agents", "Status"].map((h) => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: 10,
                      color: "#666",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      fontFamily: "'IBM Plex Mono',monospace",
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>

              {data.data.length > 0 ? (
                data.data.map((item) => (
                  <CampaignRow
                    key={item._id}
                    item={item}
                    onClick={setSelected}
                    onRepush={handleRepushAction}
                    repushLoading={
                      repushSubmitting && repushTarget?._id === item._id
                    }
                  />
                ))
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 6,
                    color: "#888",
                    fontSize: 13,
                    fontFamily: "'IBM Plex Mono',monospace",
                  }}
                >
                  No campaigns found.
                </Box>
              )}
            </Box>

            {data.totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={data.totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontFamily: "'IBM Plex Mono',monospace",
                      color: "#555",
                      fontSize: 13,
                    },
                    "& .Mui-selected": {
                      backgroundColor: "rgba(224,123,57,.15) !important",
                      color: "#c66222",
                      fontWeight: 700,
                    },
                    "& .MuiPaginationItem-root:hover": {
                      backgroundColor: "rgba(0,0,0,.04)",
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>

      <DetailModal
        item={selected}
        onClose={() => setSelected(null)}
        onExtracted={refetch}
      />

      <RepushConfirmModal
        item={repushTarget}
        onClose={closeRepushModal}
        onConfirm={confirmRepush}
        submitting={repushSubmitting}
        result={repushResult}
      />
    </>
  );
};

export default VoiceCampaignsList;
